import { Field, Form } from 'houseform'
import type React from 'react'
import { useState } from 'react'
import { clientAppLaunchBlockedReason } from '@/shared/config/client-app-launch'
import { FormField } from '@/shared/form/form-field'
import { FormLabel } from '@/shared/form/form-label'
import { FormMessages } from '@/shared/form/form-messages'
import { FormTextArea } from '@/shared/form/form-textarea'
import { AlertDialogHeader } from '@/shared/ui/alert-dialog'
import { Button } from '@/shared/ui/button'
import { Dialog, DialogContent, DialogFooter, DialogTitle, DialogTrigger } from '@/shared/ui/dialog'
import { Switch } from '@/shared/ui/switch'

interface Props {
  onSubmit?: (values: IFormChangeState) => void
  children?: React.ReactNode
  title: string
  textButtonSend?: string
  showSapToggle?: boolean
  /** Sólo al AUTORIZAR: es el momento en que la venta cierra y el acceso debería nacer. */
  showMobileAccessToggle?: boolean
  open?: boolean
  onOpenChange?: (open: boolean) => void
}

export interface IFormChangeState {
  comment: string
  skipSAP?: boolean
  /**
   * Si al autorizar hay que liberar además el acceso móvil (ADR 0024). Va **en OFF siempre**,
   * también después del lanzamiento: la invitación al cliente es manual y sólo sale cuando
   * alguien la pide a conciencia. Con la app sin publicar el switch ni siquiera se puede
   * prender, y este valor llega `false`.
   *
   * No viaja al backend con el cambio de estado: la aprobación y la liberación son dos
   * llamadas, a propósito. Emitir dentro de la transacción de aprobación haría que un
   * fallo de correo o de cuenta tumbe el cierre de una venta.
   */
  releaseMobileAccess?: boolean
}

const DialogFormChangeState = ({
  onSubmit,
  children,
  title,
  textButtonSend = 'Enviar',
  showSapToggle = false,
  showMobileAccessToggle = false,
  open: controlledOpen,
  onOpenChange
}: Props) => {
  // Con la app sin publicar el switch se VE pero no se puede prender, y lo que se manda es
  // `false` pase lo que pase. El candado va acá y no en cada llamador: es el punto por el que
  // pasan los dos efectos del switch —pintarlo y mandarlo en el submit—, así que apagarlo desde
  // un solo lugar no puede dejar medio camino prendido. Liberar acceso le manda un correo real
  // a un cliente sobre algo que no puede descargar, y un correo no se deshace.
  const launchBlockedReason = clientAppLaunchBlockedReason()
  const [internalOpen, setInternalOpen] = useState(false)
  const [skipSAP, setSkipSAP] = useState(true)
  // OFF siempre: la invitación es manual. Se vuelve a apagar al cerrar el diálogo, para que
  // abrirlo de nuevo no herede el switch que alguien prendió y canceló.
  const [releaseMobileAccessChecked, setReleaseMobileAccessChecked] = useState(false)
  const releaseMobileAccess = releaseMobileAccessChecked && launchBlockedReason === null
  const open = controlledOpen ?? internalOpen

  const setOpen = (nextOpen: boolean) => {
    if (!nextOpen) setReleaseMobileAccessChecked(false)
    if (controlledOpen === undefined) setInternalOpen(nextOpen)
    onOpenChange?.(nextOpen)
  }

  const handleSubmit = (values: IFormChangeState) => {
    onSubmit?.({
      ...values,
      ...(showSapToggle ? { skipSAP } : {}),
      ...(showMobileAccessToggle ? { releaseMobileAccess } : {})
    })
    setOpen(false)
  }

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      {children ? <DialogTrigger asChild>{children}</DialogTrigger> : null}
      <DialogContent>
        <AlertDialogHeader>
          <DialogTitle>{title}</DialogTitle>
        </AlertDialogHeader>
        <Form<IFormChangeState> onSubmit={handleSubmit}>
          {({ submit }) => {
            return (
              <form
                onSubmit={(e) => {
                  e.preventDefault()
                }}
                className="grid grid-cols-1 gap-4"
              >
                <Field name="comment" initialValue={''}>
                  {({ setValue, value, errors }) => (
                    <FormField messages={errors.map((e) => e)}>
                      <FormLabel>Comentario:</FormLabel>
                      <FormTextArea
                        value={value}
                        onChange={(e) => {
                          setValue(e.target.value)
                        }}
                      />
                      <FormMessages />
                    </FormField>
                  )}
                </Field>

                {showMobileAccessToggle && (
                  <div className="flex items-start justify-between gap-3 rounded-md border px-3 py-2">
                    <div className="flex flex-col gap-0.5">
                      <span className="text-sm font-medium">Liberar acceso en la app</span>
                      {/* Se nombra a QUIÉN le llega, no sólo qué hace el switch: es una
                          acción hacia afuera y quien aprueba tiene que poder decidirla
                          sabiendo eso. Sin lanzamiento, el motivo del switch apagado. */}
                      <span className="text-xs text-muted-foreground">
                        {launchBlockedReason ??
                          (releaseMobileAccess
                            ? 'Los propietarios podrán ver este inmueble y sus documentos publicados desde su teléfono.'
                            : 'Nadie recibirá acceso ahora. Puedes liberarlo después desde el expediente.')}
                      </span>
                    </div>
                    <Switch
                      checked={releaseMobileAccess}
                      onCheckedChange={setReleaseMobileAccessChecked}
                      disabled={launchBlockedReason !== null}
                      aria-label="Liberar acceso en la app al autorizar"
                    />
                  </div>
                )}

                {showSapToggle && (
                  <div className="flex items-center justify-between rounded-md border border-yellow-400/50 bg-yellow-400/10 px-3 py-2">
                    <div className="flex flex-col gap-0.5">
                      <span className="text-xs font-semibold text-yellow-600">
                        DEV — Envío a SAP
                      </span>
                      <span className="text-xs text-yellow-600/80">
                        {skipSAP
                          ? 'Desactivado (no se enviará a SAP)'
                          : 'Activado (se enviará a SAP)'}
                      </span>
                    </div>
                    <Switch
                      checked={!skipSAP}
                      onCheckedChange={(checked) => setSkipSAP(!checked)}
                    />
                  </div>
                )}

                <DialogFooter>
                  <Button onClick={submit}>{textButtonSend}</Button>
                </DialogFooter>
              </form>
            )
          }}
        </Form>
      </DialogContent>
    </Dialog>
  )
}

export default DialogFormChangeState
